import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { RatingContext } from './qualityAnalysis'
import ConfirmationDialog from 'live/components/actions/ui/confirmation-dialog'
import { DeviationDialogContent } from './deviationDialogContent'
import { capitalize } from 'lodash'
import { RATINGS_COLORS, RATINGS_TEXT } from '../common/utils'
import { RatingBoxDeviationInfo } from './ratingBoxDeviationInfo'
import globalNotification from 'live/services/global-notification'
import { motion } from 'framer-motion'
import { ratingBoxVariants } from './qualityRating'

interface IProps {
    ratingType: keyof RatingInfo
}
export function RatingBox({ ratingType }: IProps): JSX.Element {
    const { ratingInfo, setRatingInfo } = useContext(RatingContext)
    const [showDialog, setShowDialog] = useState(false)
    const [TempConfig, setTempConfig] = useState(ratingInfo[ratingType])

    useEffect(() => {
        setTempConfig(ratingInfo[ratingType])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDialog, ratingType])

    function handleClick(score: number): void {
        const ratingTypeObj = { ...ratingInfo[ratingType] }
        ratingTypeObj.score = score
        if (score) {
            setShowDialog(true)
        } else {
            ratingTypeObj.deviations = { allDeviations: new Set() }
            setTempConfig({ score: 0, deviations: { allDeviations: new Set() } })
        }

        setRatingInfo({ ...ratingInfo, [ratingType]: ratingTypeObj })
    }

    let preventHide = false

    function onHide(): void {
        if (preventHide) {
            preventHide = false

            return
        }

        const ratingTypeObj = { ...ratingInfo[ratingType] }

        if (
            (!TempConfig.deviations.allDeviations.size && !ratingTypeObj.deviations.allDeviations.size) ||
            (TempConfig.deviations.allDeviations.size && !ratingTypeObj.deviations.allDeviations.size)
        ) {
            ratingTypeObj.score = null
            ratingTypeObj.deviations = { allDeviations: new Set() }
            setTempConfig({ score: null, deviations: { allDeviations: new Set() } })
            setRatingInfo({ ...ratingInfo, [ratingType]: ratingTypeObj })
        }
        setShowDialog(false)
    }

    function onApply(): void {
        preventHide = true
        if (!TempConfig.deviations.allDeviations.size) {
            globalNotification.error(i18n('Please add at least one deviation if the score is 1 or above'))
        } else {
            setRatingInfo({ ...ratingInfo, [ratingType]: TempConfig })
            setShowDialog(false)
        }
    }

    return (
        <StyledRatingBox className={`ratingBox`}>
            <motion.div variants={ratingBoxVariants} className={`ratingBox--box`} key={ratingType}>
                <h2 className={`ratingBox--box__title`}>{i18n(ratingType)}</h2>
                <div className={`ratingBox--box__buttons`}>
                    {RATINGS_TEXT.map((rating, index) => (
                        <button
                            className={`ratingBox--box__buttons--${rating} ${
                                ratingInfo[ratingType].score === index ? 'selected' : null
                            }`}
                            key={rating}
                            onClick={() => handleClick(index)}
                        >
                            {index} | {i18n(rating)}
                        </button>
                    ))}
                </div>
                <RatingBoxDeviationInfo ratingType={ratingType} setShowDialog={setShowDialog} />
            </motion.div>
            {showDialog ? (
                <ConfirmationDialog
                    show={showDialog}
                    onHide={onHide}
                    title={` ${capitalize(i18n('deviations from'))} ${capitalize(i18n(ratingType))}`}
                    btnStyle={!TempConfig.deviations.allDeviations.size ? 'notAllowed' : 'primary'}
                    cancelText={i18n('cancel')}
                    className={'deviation-confirmation-modal modal-lg'}
                    content={
                        <DeviationDialogContent
                            ratingType={ratingType}
                            TempConfig={TempConfig}
                            setTempConfig={setTempConfig}
                        />
                    }
                    btnText={'OK'}
                    apply={onApply}
                />
            ) : null}
        </StyledRatingBox>
    )
}

const StyledRatingBox = styled.div`
    .ratingBox {
        &--box {
            width: 100%;
            display: flex;
            flex-direction: column;
            border: 1px solid ${({ theme }) => theme['gray-400']};
            border-radius: 3px;
            margin-bottom: 25px;

            &__title {
                padding: 15px 25px;
                text-transform: uppercase;
                font-size: 16px;
                line-height: 16px;
                font-family: ${({ theme }) => theme['font-semibold']};
                margin: 0;
                border-bottom: 1px solid ${({ theme }) => theme['gray-400']};
            }
            &__buttons {
                padding: 15px 25px;
                display: flex;
                button {
                    --color: ${RATINGS_COLORS[0]};
                    padding: 20px 0px;
                    flex: 1 1 0px;
                    border: 2px solid transparent;
                    text-transform: uppercase;
                    border-radius: 6px;
                    background: none;
                    margin-left: 8px;
                    font-family: ${({ theme }) => theme['font-semibold']};
                    color: var(--color);
                    border-color: var(--color);
                    transition: all 0.5s;
                    font-size: 15px;
                    &:first-child {
                        margin-left: 0;
                    }
                    &:nth-child(2) {
                        --color: ${RATINGS_COLORS[1]};
                    }
                    &:nth-child(3) {
                        --color: ${RATINGS_COLORS[2]};
                    }
                    &:nth-child(4) {
                        --color: ${RATINGS_COLORS[3]};
                    }
                    &:nth-child(5) {
                        --color: ${RATINGS_COLORS[4]};
                    }
                    &:hover,
                    &.selected {
                        color: ${({ theme }) => theme['gray-0']};
                        background: var(--color);
                    }
                }
            }
        }
    }
`
